import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, RotateCcw, X } from 'lucide-react'
import { getRecurringHistory, recurringHistoryKey, type RecurringItem } from '@/api/recurring'
import { HERO_SHELL } from '@/components/loans/loanHero'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatDate } from '@/lib/formatDate'
import {
  approxAmount,
  FREQUENCY_LABELS,
  getDefaultRecurringIcon,
  KIND_LABELS,
  shareSummary,
} from '@/lib/recurring'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'
import { dueBadge } from './dueLabel'
import { MarkDoneControl } from './MarkDoneControl'
import { AmountVariation } from './AmountVariation'
import { RecurringHistoryList } from './RecurringHistoryList'
import { useRecurringActions } from './useRecurringActions'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[11.5px] text-[var(--fl-text-2)]">{label}</dt>
      <dd className="mt-px text-[13px] font-medium text-[var(--fl-text-1)]">{children}</dd>
    </div>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-5 mb-1.5 text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
      {children}
    </h3>
  )
}

/** One reminder: details, actions, split, linked loan and history. Desktop: a side panel; phone: full screen. */
export function RecurringDetail({
  item,
  onClose,
  onEdit,
}: {
  item: RecurringItem
  onClose: () => void
  onEdit: (item: RecurringItem) => void
}) {
  const { format } = useFormatCurrency()
  const isReadOnly = useIsReadOnly()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const actions = useRecurringActions(item, { onEdit, onCancelled: onClose })
  const panelRef = useRef<HTMLDivElement>(null)
  const history = useQuery({
    queryKey: recurringHistoryKey(item.id),
    queryFn: () => getRecurringHistory(item.id),
  })

  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  const cancelled = !!item.cancelledAt
  const isIncome = item.type === 'INCOME'
  const badge = cancelled ? null : dueBadge(item.nextDueDate)
  const share = shareSummary(item)
  const loan = item.loanProgress
  const icon = item.icon ?? getDefaultRecurringIcon(item.kind ?? 'OTHER', item.categoryIcon)
  const kindLabel = isIncome ? 'Income' : KIND_LABELS[item.kind ?? 'OTHER']

  const body = (
    <>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--fl-hero-bg)] text-[var(--fl-hero-fg)]">
          <CategoryIcon icon={icon} size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-serif text-[22px] font-semibold text-[var(--fl-heading)]">
            {item.name}
          </h2>
          <p className="flex flex-wrap items-center gap-1.5 text-[13px] text-[var(--fl-text-2)]">
            {kindLabel} · {FREQUENCY_LABELS[item.frequency]}
            {cancelled ? (
              <span>· Cancelled {item.cancelledAt ? formatDate(item.cancelledAt) : ''}</span>
            ) : (
              <>
                {badge && <Badge tone={badge.tone}>{badge.text}</Badge>}
                <span>· due {formatDate(item.nextDueDate)}</span>
              </>
            )}
          </p>
        </div>
        <p
          className={cn(
            'shrink-0 font-serif text-[22px] font-semibold tabular-nums',
            isIncome ? 'text-[var(--fl-income)]' : 'text-[var(--fl-error)]'
          )}
        >
          {isIncome && '+'}
          {approxAmount(item, format(item.amount))}
        </p>
      </div>

      {!isReadOnly && (
        <div className="mt-4 flex flex-wrap gap-2">
          {cancelled ? (
            <Button
              variant="outline"
              size="sm"
              onClick={actions.restore}
              disabled={actions.pending}
            >
              <RotateCcw size={13} /> Restore
            </Button>
          ) : (
            <>
              <MarkDoneControl
                item={item}
                actions={actions}
                label={isIncome ? 'Got it' : 'Mark paid'}
                variant="primary"
                menuAlign="left"
              />
              <Button variant="outline" size="sm" onClick={actions.openReschedule}>
                Change due date
              </Button>
              <Button variant="outline" size="sm" onClick={actions.edit}>
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={actions.askCancel}
                className="text-[var(--fl-error)] hover:text-[var(--fl-error)]"
              >
                Cancel reminder
              </Button>
            </>
          )}
        </div>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
        <Fact label={share?.kind === 'paidBy' ? 'Paid by' : isIncome ? 'Into' : 'Paid from'}>
          {share?.kind === 'paidBy' ? share.payer : (item.financialAccountName ?? 'None')}
        </Fact>
        <Fact label="Category">{item.categoryName ?? 'None'}</Fact>
        <Fact label={isIncome ? 'Received so far' : 'Paid so far'}>
          {history.data
            ? `${format(history.data.paidTotal)} · ${history.data.paidCount} ${history.data.paidCount === 1 ? 'payment' : 'payments'}`
            : '…'}
        </Fact>
        <Fact label="Per year">{format(item.monthlyEquivalent * 12)}</Fact>
      </dl>

      {loan && item.loanAccountId && (
        <section className={cn(HERO_SHELL, 'mt-4 md:p-4')}>
          <div className="flex justify-between text-xs text-[var(--fl-hero-fg-2)]">
            <span>{item.loanAccountName}</span>
            <Link
              replace
              to={ROUTES.APP.LOAN_DETAIL(item.loanAccountId)}
              className="hover:underline"
            >
              Open loan ›
            </Link>
          </div>
          <p className="mt-0.5 font-serif text-xl font-semibold tabular-nums">
            {format(loan.outstanding)} left
          </p>
          {loan.tenureMonths != null && (
            <>
              <div
                aria-hidden="true"
                className="mt-2 h-1.5 rounded-full bg-[var(--fl-hero-bar-faint)]"
              >
                <div
                  className="h-full rounded-full bg-[var(--fl-hero-bar)]"
                  style={{
                    width: `${Math.min(100, Math.round((loan.monthsPaid / loan.tenureMonths) * 100))}%`,
                  }}
                />
              </div>
              <p className="mt-1 text-xs text-[var(--fl-hero-fg-2)]">
                {loan.monthsPaid} of {loan.tenureMonths} EMIs
              </p>
            </>
          )}
        </section>
      )}

      {share?.kind === 'chipIn' && (
        <>
          <SectionLabel>Split each time</SectionLabel>
          <div className="text-sm">
            <div className="flex justify-between border-t border-[var(--fl-divider)] py-2 first:border-t-0">
              <span>You</span>
              <span className="font-semibold tabular-nums">{format(share.yours)}</span>
            </div>
            {(item.splits ?? []).map((s) => (
              <div
                key={s.contactId}
                className="flex justify-between border-t border-[var(--fl-divider)] py-2"
              >
                <span>
                  {s.contactName ?? 'Someone'}
                  <span className="block text-xs text-[var(--fl-text-2)]">
                    Owes you after each payment
                  </span>
                </span>
                <span className="font-semibold tabular-nums">{format(s.amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {share?.kind === 'paidBy' && (
        <p className="mt-4 text-xs text-[var(--fl-text-2)]">
          {share.payer} pays this. Each cycle adds your share to what you owe {share.payer}.
        </p>
      )}

      {item.notes && (
        <>
          <SectionLabel>Notes</SectionLabel>
          <p className="whitespace-pre-wrap text-sm text-[var(--fl-text-1)]">{item.notes}</p>
        </>
      )}

      <SectionLabel>History</SectionLabel>
      {history.isLoading && <p className="text-sm text-[var(--fl-text-2)]">Loading…</p>}
      {history.isError && (
        <p className="text-sm text-[var(--fl-error)]">Couldn&apos;t load the history.</p>
      )}
      {history.data && !item.loanAccountId && (
        <AmountVariation history={history.data} expected={item.amount} />
      )}
      {history.data && (
        <RecurringHistoryList
          history={history.data}
          nextDueDate={item.nextDueDate}
          frequency={item.frequency}
        />
      )}
      {actions.dialogs}
    </>
  )

  return (
    <div
      className={cn('fixed inset-0 z-40', wide && 'bg-black/30')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={item.name}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className={cn(
          'absolute overflow-y-auto bg-[var(--fl-surface)] outline-none',
          wide
            ? 'inset-y-0 right-0 w-[440px] border-l border-[var(--fl-border)] px-[22px] py-5 shadow-[-12px_0_40px_rgba(0,0,0,0.10)] motion-safe:animate-fade-slide-up'
            : 'inset-0 px-4 pb-[max(24px,env(safe-area-inset-bottom))] pt-3'
        )}
      >
        <div className="mb-3 flex items-center justify-between">
          {wide ? (
            <span className="text-xs text-[var(--fl-text-2)]">
              {kindLabel} · {FREQUENCY_LABELS[item.frequency]}
            </span>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-[var(--fl-text-2)]"
            >
              <ArrowLeft size={18} /> Recurring
            </button>
          )}
          {wide && (
            <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose}>
              <X size={16} />
            </Button>
          )}
        </div>
        {body}
      </div>
    </div>
  )
}
